<template>
  <v-card
    tile
    color="secondary"
  >
    <v-toolbar
      flat
      max-height="56"
      color="accent"
    >
      <v-btn
        icon
        color="primary"
        :disabled="printing"
        @click.stop="close()"
      >
        <v-icon v-text="icons.close" />
      </v-btn>
    </v-toolbar>
    <v-card-text class="pa-0">
      <h-summary :amount="amount" />
      <h-monkey-mode
        v-if="monkeyMode"
        v-model="amount"
        :printing="printing"
        @paid="paid"
      />
      <template v-else>
        <h-display v-model="amount" />
        <h-calculator v-model="amount" />
        <div class="mt-2">
          <h-divide />
          <h-pay-per-items />
          <h-tip />
        </div>
        <h-customer v-model="customer" />
      </template>
    </v-card-text>
    <h-pay
      :value="amount"
      :printing="printing"
      @paid="paid"
      @print="printCheck(order)"
    />
    <h-confirm
      :value="confirmPrint"
      :title="__('printInvoice')"
      @yes="finish(true)"
      @no="finish()"
    />
    <h-tour v-model="showTour" />
    <h-reason
      v-model="printReasonDialog"
      :title="__('reprintCheck')"
      :label="__('reprintCheckReason')"
      @submit="addPrintReason"
    />
  </v-card>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import FranceLog from '@/Models/Static/FiscalCountry/FranceLog'
  import HCalculator from '@/Components/Calculator'
  import HConfirm from '@/Components/Messages/Confirm'
  import HCustomer from './Customer'
  import HDisplay from './Display'
  import HDivide from './Divide'
  import HMonkeyMode from './MonkeyMode'
  import HPay from './Pay'
  import HPayPerItems from './PayPerItems'
  import HReason from '@/Components/Reason'
  import HSummary from './Summary'
  import HTip from './Tip'
  import HTour from '@/Components/Tour/Payment'
  import I18n from '@/Mixins/I18n'
  import InteractsWithLoading from '@/Mixins/InteractsWithLoading'
  import PrintInvoice from '@/Models/Static/Business/PrintInvoice'
  import PrintsTickets from '@/Mixins/PrintsTickets'
  import ShowsTour from '@/Mixins/ShowsTour'
  import Userable from '@/Mixins/Userable'
  import UsesIcons from '@/Mixins/UsesIcons'

  export default {
    mixins: [
      I18n,
      InteractsWithLoading,
      PrintsTickets,
      ShowsTour,
      Userable,
      UsesIcons,
    ],

    components: {
      HCalculator,
      HConfirm,
      HCustomer,
      HDisplay,
      HDivide,
      HMonkeyMode,
      HPay,
      HPayPerItems,
      HReason,
      HSummary,
      HTip,
      HTour,
    },

    data: () => ({
      PrintInvoice,
      FranceLog,
      amount: '0',
      payment: {},
      change: 0,
      confirmPrint: false,
      printReasonDialog: false,
      customer: {},
      creatingInvoice: false,
    }),

    computed: {
      ...mapState([
        'UUID',
      ]),
      ...mapState('workingModes', {
        monkeyMode: 'event',
      }),
      ...mapState('orders', {
        order: 'current',
      }),
      ...mapState('orders/payment', {
        paymentMode: 'mode',
        firstPayment: 'first',
        paymentAmount: 'amountToPay',
        paidAmount: 'paidAmount',
        tipAmount: 'tipAmount',
      }),
      ...mapState('cashControl', [
        'turn',
      ]),
      ...mapGetters('orders/payment', [
        'standard',
        'payingPerItems',
        'dividing',
        'paymentMethodIsNotAccountable',
      ]),
      ...mapGetters('orders/config', [
        'alwaysChangeAlert',
        'autoChangeAlert',
      ]),
      ...mapGetters('rummage', [
        'business',
        'cashier',
      ]),
      invoice () {
        return this.order.getInvoice(this.paymentMode)
      },
      allPaid () {
        return this.order.amountToPay.lessThanOrEqualTo(0)
      },
      splittedIndexes () {
        return this.payingPerItems
          ? this.$store.state.orders.payment.payPerItems.indexes
          : this.order.orderContents.flatMap((o, index) => new Array(o.quantity - o.alreadyPaidQuantity).fill(index))
      },
      maxAmount () {
        return this.$store.getters['fiscalCountry/get']('orderMaxAmount')
      },
      maxAmountExceeded () {
        return this.maxAmount
          ? this.order.total.greaterThan(this.maxAmount)
          : false
      },
    },

    mounted () {
      if (this.shouldViewTour()) {
        setTimeout(() => this.showTour = true, 400)
      }
    },

    methods: {
      reset () {
        this.amount = '0'
        this.customer = {}
        this.creatingInvoice = false
        this.$store.dispatch('orders/payment/reset')
      },
      async paid (payment) {
        if (this.firstPayment) {
          try {
            await this.createInvoice(payment)
          } catch (e) {
            return
          }
        }
        this.addPayment(payment)
      },
      createInvoice ({ paymentMethod }) {
        return new Promise((resolve, reject) => {
          // Prevent duplicate invoice creation
          if (this.creatingInvoice) {
            this.$log('Already creating invoice, ignoring duplicate call', 'Order', this.order.id)
            reject(new Error('Already creating invoice'))
            return
          }
          
          // Check if invoice already exists for this payment mode
          const existingInvoice = this.order.getInvoice(this.paymentMode)
          if (existingInvoice) {
            this.$log('Invoice already exists, skipping creation', 'Order', this.order.id)
            resolve()
            return
          }
          
          if (this.maxAmountExceeded && ! this.customer?.invoiceable) {
            this.$store.dispatch('alert/showWarning', this.__('assignInvoiceableCustomer'))
            reject()
            return
          }
          
          this.creatingInvoice = true
          this.startLoading()
          this.$log('Creating invoice...', 'Order', this.order.id)
          this.$api(['getNewId', 'Invoice'], {
            order_id: this.order.id,
            cashier_id: this.cashier?.id,
            notAccountable: this.paymentMethodIsNotAccountable(paymentMethod),
            customer_id: this.customer.id ?? null,
          })
            .then(r => {
              this.stopLoading()
              this.creatingInvoice = false
              this.$store.commit('orders/createInvoice', {
                data: {
                  ...r.data,
                  paymentMode: this.paymentMode,
                  total: this.payingPerItems ? this.paymentAmount : this.order.amountToPay,
                },
                customer: this.customer,
              })
              this.$log(`Created invoice: ${r.data.id}`, 'Order', this.order.id)
              resolve()
            })
            .catch(e => {
              this.stopLoading()
              this.creatingInvoice = false
              this.$store.dispatch('xError', e)
              reject()
            })
            // Note that we do not stop the loading in the finally() method
            // because it gave us some issues
        })
      },
      addPayment ({ paymentMethod, payAmount, tipAmount, change, voidReason }) {
        this.payment = {
          payAmount,
          paymentMethod,
          change,
          tipAmount,
          tenantUser_id: this.user.id,
          tenantUserName: this.user.name,
          turn_id: this.turn?.id,
          voidReason,
        }
        this.$store.commit('orders/addPayment', {
          mode: this.paymentMode,
          payment: this.payment,
        })
        if (this.paymentMethodIsNotAccountable(paymentMethod)) {
          this.$store.commit('orders/setCurrentInvoiceToNotAccountable', this.paymentMode)
        }
        this.invoice.isPaid || this.allPaid
          ? this.completeInvoice()
          : this.commitPayment()
      },
      commitPayment () {
        this.$store.dispatch('orders/payment/pay', this.payment.payAmount)
        this.showChangeAlert(this.payment.change)
        this.amount = '0'
      },
      completeInvoice () {
        this.$store.commit('orders/splitContents', {
          invoice: this.invoice,
          indexes: this.splittedIndexes,
        })
        this.$store.commit('orders/invoiceSummary', this.invoice)
        this.printInvoiceStrategy()
      },
      printInvoiceStrategy () {
        switch (this.business('printInvoice')) {
          case this.PrintInvoice.MAIL:
          case this.PrintInvoice.ASK:
            this.confirmPrint = true
            break
          case this.PrintInvoice.NEVER:
            this.finish()
            break
          default:
            this.finish(true)
        }
      },
      async finish (print = false) {
        this.confirmPrint = false
        if (print) {
          try {
            await this.signInvoice(this.invoice)
            await this.printInvoice(this.invoice)
          } catch (e) {
            this.$store.dispatch('alert/showError', this.__('printError'))
            this.$log('Print error', 'Invoice', this.invoice.id)
            this.$logFrance(this.FranceLog.PRINTER_NOT_AVAILABLE, `Invoice ${this.invoice.id}`)
          }
        }
        this.commitPayment()
        this.customer = {}
        if (this.allPaid) {
          this.$emit('paid')
        }
      },
      async signInvoice (invoice) {
        if (! this.$store.getters['fiscalCountry/shouldSignInvoice']) {
          return
        }
        await this.saveOrder() // REV-9517
        await this.$store.dispatch('fiscalCountry/signInvoice', invoice)
      },
      addPrintReason (reason) {
        this.signPrintCheck(this.order, reason)
      },
      saveOrder () {
        this.$store.commit('orders/linkInvoicesWithContents')
        this.$log('Saving before signing...', 'Order', this.order.id)
        return this.$api(['save', 'Order'], {
          ...this.order,
          saveToken: String.random(32),
          lockedBy: this.UUID,
        })
          .then(r => {
            this.$log('Saved before signing', 'Order', this.order.id)
            this.$store.commit('orders/current', r.data)
            this.$store.commit('orders/forceSave')
          })
      },
      shouldShowChangeAlert (change) {
        // copied from Xef's Taste/Controllers/Order/Cash/GSCashViewController.m
        return this.alwaysChangeAlert || (this.autoChangeAlert && change.greaterThan(0))
      },
      showChangeAlert (change) {
        if (! this.shouldShowChangeAlert(change)) {
          return
        }
        this.$store.dispatch('alert/snackSuccess', [
          this.__('paymentDone'),
          this.__('change') + ': ' + change.toFixed(2),
        ])
      },
      close () {
        this.reset()
        this.$store.dispatch('orders/dialogs/close')
      },
    },
  }
</script>
